/**
 * Notification Type System
 * Defines core notification categories, their behavior, and default configurations
 */

export const NOTIFICATION_TYPES = {
  // Behavior Incidents
  BEHAVIOR: {
    id: 'behavior',
    name: 'Behavior Incidents',
    description: 'Discipline issues, rule violations, and behavior concerns',
    icon: 'ExclamationTriangleIcon',
    color: 'warning', // daisyUI semantic color
    priority: 'high',
    defaultChannels: ['telegram'],
    timing: 'immediate',
    requireAction: true,
    schoolConfigurable: true,
    userConfigurable: ['channels', 'priority'],
    templates: {
      immediate: 'behavior_alert',
      daily: 'behavior_digest',
      weekly: 'behavior_weekly'
    }
  },

  // Attendance
  ATTENDANCE: {
    id: 'attendance',
    name: 'Attendance',
    description: 'Absences, tardiness, and attendance patterns',
    icon: 'CalendarIcon',
    color: 'info',
    priority: 'medium',
    defaultChannels: ['telegram'],
    timing: 'immediate',
    requireAction: false,
    schoolConfigurable: true,
    userConfigurable: ['channels', 'priority'],
    templates: {
      immediate: 'attendance_alert',
      daily: 'attendance_digest',
      weekly: 'attendance_weekly'
    }
  },

  // Achievements
  ACHIEVEMENTS: {
    id: 'achievements',
    name: 'Achievements',
    description: 'Student accomplishments, awards, and positive recognition',
    icon: 'TrophyIcon',
    color: 'success',
    priority: 'low',
    defaultChannels: ['email'],
    timing: 'daily',
    requireAction: false,
    schoolConfigurable: true,
    userConfigurable: ['channels', 'priority', 'timing'],
    templates: {
      immediate: 'achievement_alert',
      daily: 'achievement_digest',
      weekly: 'achievement_weekly'
    }
  },

  // Announcements
  ANNOUNCEMENTS: {
    id: 'announcements',
    name: 'Announcements',
    description: 'School-wide announcements, notices, and important information',
    icon: 'MegaphoneIcon',
    color: 'info',
    priority: 'medium',
    defaultChannels: ['email'],
    timing: 'daily',
    requireAction: false,
    schoolConfigurable: true,
    userConfigurable: ['channels', 'priority', 'timing'],
    templates: {
      immediate: 'announcement_urgent',
      daily: 'announcement_digest',
      weekly: 'announcement_weekly'
    }
  },

  // Emergencies
  EMERGENCIES: {
    id: 'emergencies',
    name: 'Emergencies',
    description: 'Urgent safety alerts and critical information',
    icon: 'ShieldExclamationIcon',
    color: 'error',
    priority: 'critical',
    // SMS omitted until an SMS provider is wired up — the channel stays defined
    // below as a disabled placeholder, but listing it as a default makes the
    // config fail its own validation (validChannels excludes disabled channels).
    defaultChannels: ['telegram', 'email'],
    timing: 'immediate',
    requireAction: true,
    schoolConfigurable: false, // Cannot be disabled
    userConfigurable: [], // No user overrides for emergencies
    templates: {
      immediate: 'emergency_alert'
    }
  },

  // Homework
  HOMEWORK: {
    id: 'homework',
    name: 'Homework',
    description: 'Assignments, due dates, and homework-related updates',
    icon: 'BookOpenIcon',
    color: 'info',
    priority: 'medium',
    defaultChannels: ['telegram'],
    timing: 'immediate',
    requireAction: false,
    schoolConfigurable: true,
    userConfigurable: ['channels', 'priority', 'timing'],
    templates: {
      immediate: 'homework_assigned',
      daily: 'homework_reminder',
      weekly: 'homework_summary'
    }
  },

  // Reminders
  REMINDERS: {
    id: 'reminders',
    name: 'Reminders',
    description: 'Event reminders, deadline notifications, and schedule updates',
    icon: 'BellIcon',
    color: 'base-content',
    priority: 'low',
    defaultChannels: ['telegram'],
    timing: 'morning',
    requireAction: false,
    schoolConfigurable: true,
    userConfigurable: ['channels', 'priority', 'timing'],
    templates: {
      immediate: 'reminder_urgent',
      daily: 'reminder_daily',
      weekly: 'reminder_weekly'
    }
  }
};

export const NOTIFICATION_PRIORITIES = {
  CRITICAL: {
    id: 'critical',
    name: 'Critical',
    description: 'Requires immediate attention',
    color: 'error',
    timing: 'immediate',
    bypassQuietHours: true,
    requireConfirmation: true
  },
  HIGH: {
    id: 'high',
    name: 'High',
    description: 'Important but not urgent',
    color: 'warning',
    timing: 'immediate',
    bypassQuietHours: false,
    requireConfirmation: false
  },
  MEDIUM: {
    id: 'medium',
    name: 'Medium',
    description: 'Standard importance',
    color: 'info',
    timing: 'batched',
    bypassQuietHours: false,
    requireConfirmation: false
  },
  LOW: {
    id: 'low',
    name: 'Low',
    description: 'Informational only',
    color: 'base-content',
    timing: 'daily',
    bypassQuietHours: false,
    requireConfirmation: false
  }
};

export const NOTIFICATION_TIMING = {
  IMMEDIATE: {
    id: 'immediate',
    name: 'Immediate',
    description: 'Send right away',
    delay: 0,
    batchingEnabled: false
  },
  MORNING: {
    id: 'morning',
    name: 'Morning',
    description: 'Send at 8 AM',
    delay: '08:00',
    batchingEnabled: false
  },
  BATCHED: {
    id: 'batched',
    name: 'Batched',
    description: 'Batch with similar notifications',
    delay: null, // Use school batch time
    batchingEnabled: true
  },
  DAILY: {
    id: 'daily',
    name: 'Daily',
    description: 'Include in daily summary',
    delay: null, // Use school daily time
    batchingEnabled: true
  },
  WEEKLY: {
    id: 'weekly',
    name: 'Weekly',
    description: 'Include in weekly summary',
    delay: null, // Use school weekly time
    batchingEnabled: true
  }
};

export const NOTIFICATION_CHANNELS = {
  TELEGRAM: {
    id: 'telegram',
    name: 'Telegram',
    description: 'Instant messaging via Telegram bot',
    icon: 'ChatBubbleLeftRightIcon',
    enabled: true,
    supportsActions: true,
    supportsRichContent: true,
    maxLength: 4096,
    optInRequired: false
  },
  EMAIL: {
    id: 'email',
    name: 'Email',
    description: 'Email notifications',
    icon: 'EnvelopeIcon',
    enabled: true,
    supportsActions: true,
    supportsRichContent: true,
    maxLength: null,
    optInRequired: true
  },
  SMS: {
    id: 'sms',
    name: 'SMS',
    description: 'Text messages',
    icon: 'DevicePhoneIcon',
    enabled: false, // Requires setup
    supportsActions: false,
    supportsRichContent: false,
    maxLength: 160,
    optInRequired: true
  },
  IN_APP: {
    id: 'inApp',
    name: 'In-App',
    description: 'Notifications within the app',
    icon: 'BellIcon',
    enabled: true,
    supportsActions: true,
    supportsRichContent: true,
    maxLength: null,
    optInRequired: false
  }
};

/**
 * Get notification type configuration
 */
export const getNotificationType = (typeId) => {
  return NOTIFICATION_TYPES[typeId.toUpperCase()] || null;
};

/**
 * Get priority configuration
 */
export const getPriorityConfig = (priorityId) => {
  return NOTIFICATION_PRIORITIES[priorityId.toUpperCase()] || null;
};

/**
 * Get timing configuration
 */
export const getTimingConfig = (timingId) => {
  return NOTIFICATION_TIMING[timingId.toUpperCase()] || null;
};

/**
 * Get channel configuration
 */
export const getChannelConfig = (channelId) => {
  return NOTIFICATION_CHANNELS[channelId.toUpperCase()] || null;
};

/**
 * Get all notification types as array
 */
export const getAllNotificationTypes = () => {
  return Object.values(NOTIFICATION_TYPES);
};

/**
 * Get available priorities as array
 */
export const getAllPriorities = () => {
  return Object.values(NOTIFICATION_PRIORITIES);
};

/**
 * Get available timing options as array
 */
export const getAllTimingOptions = () => {
  return Object.values(NOTIFICATION_TIMING);
};

/**
 * Get available channels as array
 */
export const getAllChannels = () => {
  return Object.values(NOTIFICATION_CHANNELS).filter(channel => channel.enabled);
};

/**
 * Check if a notification type is school configurable
 */
export const isSchoolConfigurable = (typeId) => {
  const type = getNotificationType(typeId);
  return type?.schoolConfigurable ?? false;
};

/**
 * Check if user can override specific settings for a notification type
 */
export const getUserConfigurableSettings = (typeId) => {
  const type = getNotificationType(typeId);
  return type?.userConfigurable ?? [];
};

/**
 * Get default configuration for a notification type
 */
export const getDefaultConfig = (typeId, schoolSettings = {}) => {
  const type = getNotificationType(typeId);
  if (!type) return null;

  return {
    enabled: true,
    channels: type.defaultChannels,
    priority: type.priority,
    timing: type.timing,
    // Override with school defaults if available
    ...(schoolSettings.defaultChannels?.[typeId] || {})
  };
};

/**
 * Validate notification configuration
 */
export const validateNotificationConfig = (config) => {
  const errors = [];

  // Check required fields
  if (!config.channels || config.channels.length === 0) {
    errors.push('At least one channel must be selected');
  }

  if (!config.priority) {
    errors.push('Priority is required');
  }

  if (!config.timing) {
    errors.push('Timing is required');
  }

  // Validate channels exist
  const validChannels = getAllChannels().map(c => c.id);
  const invalidChannels = config.channels?.filter(c => !validChannels.includes(c)) || [];
  if (invalidChannels.length > 0) {
    errors.push(`Invalid channels: ${invalidChannels.join(', ')}`);
  }

  // Validate priority
  const validPriorities = getAllPriorities().map(p => p.id);
  if (!validPriorities.includes(config.priority)) {
    errors.push(`Invalid priority: ${config.priority}`);
  }

  // Validate timing
  const validTimings = getAllTimingOptions().map(t => t.id);
  if (!validTimings.includes(config.timing)) {
    errors.push(`Invalid timing: ${config.timing}`);
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export default {
  NOTIFICATION_TYPES,
  NOTIFICATION_PRIORITIES,
  NOTIFICATION_TIMING,
  NOTIFICATION_CHANNELS,
  getNotificationType,
  getPriorityConfig,
  getTimingConfig,
  getChannelConfig,
  getAllNotificationTypes,
  getAllPriorities,
  getAllTimingOptions,
  getAllChannels,
  isSchoolConfigurable,
  getUserConfigurableSettings,
  getDefaultConfig,
  validateNotificationConfig
};